import React, {
  useContext,
  useState,
  ChangeEvent,
  FocusEvent,
  FormEvent,
} from "react";
import { Box, Grid } from "@mui/material";
import { useNavigate } from "react-router";
import {
  Alert,
  Button,
  Link,
  TextField,
  Typography,
} from "@platform/design-system";
import { LogoWhite } from "@platform/portal-ui";

import { isValidEmail } from "../../utils";
import { AuthContext } from "../../contexts/authContext";
import { LoginPage, LOGIN_PAGE_TEST_ID } from "../../components/loginPage";
import PasswordSent from "../../components/passwordSent";

// The assets barrel is a .js module, so its exports are untyped here.
const logoWhite: unknown = LogoWhite;
const logoWhiteSrc = typeof logoWhite === "string" ? logoWhite : "";

const EMAIL = "email";

// Type for the component's state
interface State {
  [EMAIL]: string;
  apiError: string | null;
}

// Type for errors
interface Errors {
  [key: string]: string | null;
}

const extractResponseMessage = (error: unknown): string | null => {
  if (typeof error !== "object" || error === null || !("response" in error))
    return null;
  const { response } = error;
  if (
    typeof response !== "object" ||
    response === null ||
    !("data" in response)
  )
    return null;
  const { data } = response;
  if (typeof data !== "object" || data === null || !("message" in data))
    return null;
  return typeof data.message === "string" ? data.message : null;
};

const ResetPassword: React.FC = () => {
  const [state, setState] = useState<State>({
    [EMAIL]: "",
    apiError: null,
  });
  const navigate = useNavigate();
  const [passwordSent, setPasswordSent] = useState<boolean>(false);
  const [errors, setErrors] = useState<Errors>({});
  const [loading, setLoading] = useState<boolean>(false);
  // Reads the typed context directly: the useAuth() hook lives in a .js
  // module, which leaves its return value untyped here.
  const auth = useContext(AuthContext);
  if (!auth) {
    throw new Error("ResetPassword must be used within an AuthProvider");
  }
  const { resetPassword } = auth;

  const isValidInfo =
    !Object.values(errors).some((e) => e !== null) &&
    !!state[EMAIL] &&
    isValidEmail(state[EMAIL]);

  const handleSubmit = async (
    e: FormEvent<HTMLFormElement> | FormEvent<HTMLButtonElement>,
  ) => {
    e.preventDefault();
    try {
      setLoading(true);
      await resetPassword(state[EMAIL]);
      setPasswordSent(true);
    } catch (error: unknown) {
      setLoading(false);
      setState((prev) => ({
        ...prev,
        apiError: extractResponseMessage(error) || "An error occurred",
      }));
    }
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (!value) {
      setErrors((prev) => ({ ...prev, [name]: "Please complete the field." }));
    } else if (name === EMAIL && !isValidEmail(value)) {
      setErrors((prev) => ({ ...prev, [name]: "Invalid email." }));
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setState((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleBackToSignIn = () => {
    navigate("/");
  };

  return (
    <LoginPage
      container
      data-testid={LOGIN_PAGE_TEST_ID}
      justifyContent="space-between"
    >
      {!passwordSent ? (
        <Grid
          size={3}
          margin={"auto 6rem"}
          display={"flex"}
          flexDirection={"column"}
          gap={"32px"}
        >
          <>
            <Typography variant="display-medium">Reset Password</Typography>
            {state.apiError && (
              <Box mt={2} mb={2}>
                <Alert severity="error">{state.apiError}</Alert>
              </Box>
            )}
            <Typography variant="body-medium">
              Enter your email address in the box below. If you have a
              registered Centro account, we will send you a reset password link.
            </Typography>

            <Box
              component="form"
              display="flex"
              flexDirection="column"
              gap={2}
              onSubmit={handleSubmit}
              mb={2}
              mt={4}
            >
              <TextField
                fullWidth
                value={state[EMAIL]}
                onChange={handleChange}
                onBlur={handleBlur}
                name={EMAIL}
                error={!!errors[EMAIL]}
                helperText={errors[EMAIL]}
                label="Email address"
              />

              <Button
                fullWidth
                variant="primary"
                type="submit"
                disabled={!isValidInfo || loading}
                loading={loading}
                onClick={handleSubmit}
                sx={{ py: 1, mt: 1 }}
              >
                Reset Password
              </Button>
            </Box>
            <Box>
              <Link
                component="button"
                variant="body2"
                onClick={handleBackToSignIn}
                sx={{ fontWeight: "bold" }}
              >
                Back to sign in
              </Link>
            </Box>
          </>
        </Grid>
      ) : (
        <PasswordSent heading="Reset Password" email={state[EMAIL]} />
      )}
      <Grid size={5} display="flex" alignItems="center" className="login-logo">
        <Box component="img" src={logoWhiteSrc} alt="centro thumb" />
      </Grid>
    </LoginPage>
  );
};

export default ResetPassword;
